import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from "react";

type AlertTone = "neutral" | "danger" | "success";

export function Alert({ tone = "neutral", children, className, ...props }: HTMLAttributes<HTMLDivElement> & { tone?: AlertTone }) {
  const Icon = tone === "success" ? CheckCircle2 : AlertCircle;
  return (
    <div className={["ui-alert", `ui-alert--${tone}`, className].filter(Boolean).join(" ")} role={tone === "danger" ? "alert" : "status"} {...props}>
      <Icon aria-hidden="true" size={18} />
      <div>{children}</div>
    </div>
  );
}

type ToastProps = Parameters<typeof Alert>[0] & {
  duration?: number;
  onDismiss: () => void;
};

const TOAST_DURATION_MS = 3400;
const TOAST_EXIT_MS = 240;

export function Toast({ children, className, duration = TOAST_DURATION_MS, onDismiss, ...props }: ToastProps) {
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const exitTimerRef = useRef<number | undefined>(undefined);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const beginDismiss = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    exitTimerRef.current = window.setTimeout(() => onDismissRef.current(), TOAST_EXIT_MS);
  }, []);

  useEffect(() => {
    const displayTimer = window.setTimeout(beginDismiss, duration);
    return () => {
      window.clearTimeout(displayTimer);
      window.clearTimeout(exitTimerRef.current);
    };
  }, [beginDismiss, duration]);

  return (
    <Alert className={["ui-toast", leaving ? "is-leaving" : "", className].filter(Boolean).join(" ")} {...props}>
      <span>{children}</span>
      <button type="button" aria-label="Dismiss notification" onClick={beginDismiss}>×</button>
    </Alert>
  );
}

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={["ui-skeleton", className].filter(Boolean).join(" ")} aria-hidden="true" {...props} />;
}

type EmptyProps = {
  title: string;
  description: string;
  action?: ReactNode;
};

export function EmptyState({ title, description, action }: EmptyProps) {
  return <section className="ui-empty"><h2>{title}</h2><p>{description}</p>{action}</section>;
}

export function NoResults({ title = "No results", description = "Try changing your search or filters." }: Partial<EmptyProps>) {
  return <section className="ui-no-results"><h2>{title}</h2><p>{description}</p></section>;
}
